import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  Agent,
  AgentEntryList,
  AgentStats,
  AgentChatMessage,
} from 'api/agent/types';

interface AgentState {
  agentInfo: Agent | null;
  isOwner: boolean;
  loading: boolean;

  // Entry history
  agentEntryListInfo: AgentEntryList;
  agentEntryListCurPage: number;
  entryScrollLoading: boolean;

  // Stats
  agentStats: AgentStats | null;
  statsPeriod: string;

  // Chat
  chatMessages: AgentChatMessage[];
  chatLoading: boolean;

  // Scan
  scanLoading: boolean;
  lastScanResult: { scanned: number; entered: number; skipped: number } | null;
}

const initialState: AgentState = {
  agentInfo: null,
  isOwner: false,
  loading: false,

  agentEntryListInfo: {
    has_next: false,
    list: [],
    page: 1,
    size: 20,
    total: -1,
  },
  agentEntryListCurPage: 1,
  entryScrollLoading: false,

  agentStats: null,
  statsPeriod: '7d',

  chatMessages: [],
  chatLoading: false,

  scanLoading: false,
  lastScanResult: null,
};

export const agentSlice = createSlice({
  name: 'agentSlice',
  initialState,
  reducers: {
    setAgentInfo: (state, action: PayloadAction<Agent | null>) => {
      state.agentInfo = action.payload;
    },
    setIsOwner: (state, action: PayloadAction<boolean>) => {
      state.isOwner = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Entry history
    setAgentEntryListInfo: (state, action: PayloadAction<AgentEntryList>) => {
      state.agentEntryListInfo.has_next = action.payload?.has_next;
      state.agentEntryListInfo.list =
        state.agentEntryListCurPage === 1
          ? action.payload?.list || []
          : state.agentEntryListInfo.list.concat(action.payload?.list);
      state.agentEntryListInfo.page = action.payload?.page;
      state.agentEntryListInfo.total = action.payload?.total;
    },
    setAgentEntryListCurPage: (state, action: PayloadAction<number>) => {
      state.agentEntryListCurPage = action.payload;
    },
    setEntryScrollLoading: (state, action: PayloadAction<boolean>) => {
      state.entryScrollLoading = action.payload;
    },

    // Stats
    setAgentStats: (state, action: PayloadAction<AgentStats | null>) => {
      state.agentStats = action.payload;
    },
    setStatsPeriod: (state, action: PayloadAction<string>) => {
      state.statsPeriod = action.payload;
    },

    // Chat
    addChatMessage: (state, action: PayloadAction<AgentChatMessage>) => {
      state.chatMessages.push(action.payload);
    },
    setChatLoading: (state, action: PayloadAction<boolean>) => {
      state.chatLoading = action.payload;
    },
    clearChatMessages: (state) => {
      state.chatMessages = [];
    },

    // Scan
    setScanLoading: (state, action: PayloadAction<boolean>) => {
      state.scanLoading = action.payload;
    },
    setLastScanResult: (
      state,
      action: PayloadAction<{
        scanned: number;
        entered: number;
        skipped: number;
      } | null>
    ) => {
      state.lastScanResult = action.payload;
    },

    // Reset
    resetAgentState: () => initialState,
  },
});

export const {
  setAgentInfo,
  setIsOwner,
  setLoading,
  setAgentEntryListInfo,
  setAgentEntryListCurPage,
  setEntryScrollLoading,
  setAgentStats,
  setStatsPeriod,
  addChatMessage,
  setChatLoading,
  clearChatMessages,
  setScanLoading,
  setLastScanResult,
  resetAgentState,
} = agentSlice.actions;

export default agentSlice.reducer;
